/**
 * BRXEmailValidation
 *
 * Client-side email syntax checking, domain typo suggestion, and inline error
 * rendering for the BRX capture forms (popup-form-ac.liquid, footer-form.liquid).
 *
 * The backend validates deliverability with ZeroBounce on every submit. This
 * module exists to catch the obvious mistakes — mistyped domains and TLDs —
 * before they cost a lookup, and to render the server's own verdict (including
 * its did_you_mean) when one comes back.
 *
 * A suggestion NEVER makes check() return ok:false. Deciding whether a
 * suggestion blocks a submit is the caller's job; both snippets block once and
 * then let an unchanged re-submit through.
 *
 * FALSE POSITIVES: if a real domain is being "corrected", the fix is to add it
 * to DOMAINS. An exact list match is never corrected, so listing a domain
 * immunizes it. Several DOMAINS entries (email.com, ymail.com, mymail.com) are
 * there for exactly that reason rather than for their popularity.
 *
 * Usage:
 * - check(raw) — { ok, value, error, suggestion, reason }
 * - validateField(input, opts) — check + write back + render, returns the result
 * - render(input, opts) / clear(input) — inline error and correction UI
 * - parseServerRejection(json) — shopper-facing copy from a 422 body
 */
(function () {
    const VERSION = '1.0.0';

    const MESSAGES = Object.freeze({
        required: 'This field is required.',
        invalid: 'Enter a valid email address.',
        didYouMean: 'Did you mean',
        generic: 'Please check your email address and try again.',
        network: 'Something went wrong. Please try again.'
    });

    // Ordered by popularity, and the order is load-bearing: bestMatch breaks
    // distance ties by list index, so the most likely candidate must come first.
    // Do not sort this alphabetically.
    const DOMAINS = Object.freeze([
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
        'aol.com', 'me.com', 'live.com', 'msn.com', 'comcast.net', 'verizon.net',
        'att.net', 'sbcglobal.net', 'bellsouth.net', 'cox.net', 'charter.net',
        'ymail.com', 'googlemail.com', 'protonmail.com', 'proton.me', 'pm.me',
        'mail.com', 'email.com', 'mymail.com', 'yandex.com', 'zoho.com',
        'gmx.com', 'gmx.net', 'hotmail.co.uk', 'yahoo.co.uk', 'live.co.uk',
        'btinternet.com', 'rocketmail.com', 'earthlink.net', 'optonline.net',
        'roadrunner.com', 'juno.com', 'aim.com', 'mac.com', 'shaw.ca',
        'rogers.com', 'telus.net', 'hey.com', 'fastmail.com'
    ]);

    const SECOND_LEVEL = Object.freeze([
        'gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'live', 'msn',
        'comcast', 'verizon', 'att', 'sbcglobal', 'bellsouth', 'cox', 'charter',
        'ymail', 'googlemail', 'protonmail', 'proton', 'mail', 'email', 'mymail',
        'yandex', 'zoho', 'gmx', 'btinternet', 'rocketmail', 'earthlink',
        'optonline', 'roadrunner', 'juno', 'aim', 'mac', 'shaw', 'rogers',
        'telus', 'hey', 'fastmail', 'me'
    ]);

    // A TLD in this list is NEVER rewritten. That is the whole mechanism
    // protecting .co, .me, .io, .us and .ca from being pulled to .com.
    // Note .cm is deliberately ABSENT: it is a real ccTLD, but for this
    // storefront it is overwhelmingly a .com slip worth suggesting against.
    const TLDS = Object.freeze([
        'com', 'net', 'org', 'edu', 'gov', 'co', 'io', 'us', 'ca', 'uk', 'au',
        'de', 'fr', 'es', 'it', 'nl', 'se', 'no', 'dk', 'fi', 'ie', 'nz', 'br',
        'mx', 'jp', 'in', 'info', 'biz', 'me', 'dev', 'app', 'xyz', 'online',
        'site', 'shop', 'store', 'email',
        'co.uk', 'com.au', 'co.nz', 'co.jp', 'com.br', 'co.za'
    ]);

    const MULTI_TLDS = Object.freeze(TLDS.filter((tld) => tld.indexOf('.') !== -1));

    // Only these are reachable from a two-character typo, and only by a single
    // insertion. Keeps `cm` -> `com` while leaving every real 2-char ccTLD alone.
    const SHORT_TLD_TARGETS = Object.freeze(['com', 'net', 'org']);

    // Exact misspelling -> canonical. Edit distance cannot safely reach the long
    // tail of scrambles (hsmil -> gmail is three edits on a five-character
    // label), so known-bad spellings are listed outright. Adding an entry here
    // is exact-match only and therefore risk-free.
    const DOMAIN_ALIASES = Object.freeze({
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'gmaill.com': 'gmail.com',
        'gmailc.com': 'gmail.com',
        'gmailcom': 'gmail.com',
        'gmail.comm': 'gmail.com',
        'gnail.com': 'gmail.com',
        'gamil.com': 'gmail.com',
        'gmali.com': 'gmail.com',
        'ggmail.com': 'gmail.com',
        'hsmil.com': 'gmail.com',
        'hmail.com': 'gmail.com',
        'gmail.clm': 'gmail.com',
        'gmaul.com': 'gmail.com',
        'gmail.cim': 'gmail.com',
        'yahoo.cpm': 'yahoo.com',
        'yaho.com': 'yahoo.com',
        'yahho.com': 'yahoo.com',
        'yhoo.com': 'yahoo.com',
        'yahooo.com': 'yahoo.com',
        'yahoo.om': 'yahoo.com',
        'hotmial.com': 'hotmail.com',
        'hotmai.com': 'hotmail.com',
        'hotmal.com': 'hotmail.com',
        'hotnail.com': 'hotmail.com',
        'hotmaill.com': 'hotmail.com',
        'hotmail.cm': 'hotmail.com',
        'homail.com': 'hotmail.com',
        'hoymail.com': 'hotmail.com',
        'outlok.com': 'outlook.com',
        'outllok.com': 'outlook.com',
        'outook.com': 'outlook.com',
        'outlool.com': 'outlook.com',
        'oulook.com': 'outlook.com',
        'iclould.com': 'icloud.com',
        'iclod.com': 'icloud.com',
        'icoud.com': 'icloud.com',
        'icloude.com': 'icloud.com',
        'iclous.com': 'icloud.com',
        'aol.cm': 'aol.com',
        'aoll.com': 'aol.com',
        'comcast.com': 'comcast.net',
        'verizon.com': 'verizon.net',
        'att.com': 'att.net',
        'sbcglobal.com': 'sbcglobal.net',
        'bellsouth.com': 'bellsouth.net',
        'protonmai.com': 'protonmail.com',
        'protonmail.co': 'protonmail.com',
        'live.cm': 'live.com',
        'msn.cm': 'msn.com'
    });

    const LISTS = Object.freeze({ DOMAINS, SECOND_LEVEL, TLDS, MULTI_TLDS, DOMAIN_ALIASES });

    /**
     * Maximum edit distance allowed when matching a second-level domain.
     * Zero at four characters or fewer: short labels are too easy to reach by
     * accident, so they are never guessed at.
     */
    const secondLevelThreshold = (len) => (len <= 4 ? 0 : len <= 7 ? 1 : 2);

    /**
     * Optimal-string-alignment Damerau-Levenshtein distance.
     *
     * Transpositions cost 1, which is what makes `gmial` -> `gmail` and
     * `ocm` -> `com` single-edit fixes. Bails out with max + 1 as soon as an
     * entire row exceeds max, so a domain nowhere near a candidate exits early.
     */
    function distance(a, b, max) {
        const limit = typeof max === 'number' ? max : Infinity;
        const al = a.length;
        const bl = b.length;

        if (Math.abs(al - bl) > limit) return limit + 1;
        if (!al) return bl;
        if (!bl) return al;

        let prev2 = null;
        let prev = new Array(bl + 1);
        let curr = new Array(bl + 1);
        for (let j = 0; j <= bl; j++) prev[j] = j;

        for (let i = 1; i <= al; i++) {
            curr[0] = i;
            let rowMin = curr[0];

            for (let j = 1; j <= bl; j++) {
                const cost = a[i - 1] === b[j - 1] ? 0 : 1;
                let d = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
                if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                    d = Math.min(d, prev2[j - 2] + cost);
                }
                curr[j] = d;
                if (d < rowMin) rowMin = d;
            }

            if (rowMin > limit) return limit + 1;
            prev2 = prev;
            prev = curr;
            curr = new Array(bl + 1);
        }

        return prev[bl];
    }

    /**
     * Closest list entry within maxDistance, or null.
     * Ties break by list index, so the earliest (most popular) candidate wins.
     * An exact match returns null — a known-good value is never "corrected".
     */
    function bestMatch(value, list, maxDistance) {
        let best = null;
        let bestDistance = maxDistance + 1;

        for (let i = 0; i < list.length; i++) {
            const candidate = list[i];
            if (candidate === value) return null;
            const d = distance(value, candidate, bestDistance - 1);
            if (d < bestDistance) {
                bestDistance = d;
                best = candidate;
            }
        }

        return bestDistance <= maxDistance ? best : null;
    }

    /**
     * Split a domain into { prefix, sld, tld, labels }.
     * A `co.uk`-style suffix counts as one TLD; `prefix` holds any subdomain
     * labels and is preserved verbatim.
     *
     * mail.google.com -> { prefix: 'mail', sld: 'google', tld: 'com', labels: 3 }
     * yahoo.co.uk     -> { prefix: '',     sld: 'yahoo',  tld: 'co.uk', labels: 3 }
     */
    function splitDomain(domain) {
        const parts = String(domain || '').split('.');
        const empty = { prefix: '', sld: '', tld: '', labels: parts.length };
        if (parts.length < 2) return empty;

        const multi = MULTI_TLDS.find((tld) => domain.endsWith('.' + tld));
        const tldLabels = multi ? multi.split('.').length : 1;
        const head = parts.slice(0, parts.length - tldLabels);
        if (!head.length) return empty;

        return {
            prefix: head.slice(0, -1).join('.'),
            sld: head[head.length - 1],
            tld: parts.slice(parts.length - tldLabels).join('.'),
            labels: parts.length
        };
    }

    /**
     * A corrected TLD, or null. A TLD already in TLDS is never rewritten.
     */
    function suggestTld(tld) {
        if (!tld || TLDS.indexOf(tld) !== -1) return null;

        if (tld.length >= 3) return bestMatch(tld, TLDS, 1);

        if (tld.length === 2) {
            // Reachable only by inserting one character into a target, e.g.
            // cm -> com, nt -> net. Anything looser would start rewriting real
            // two-letter ccTLDs that simply aren't in our list.
            return SHORT_TLD_TARGETS.find((target) => (
                target.length === tld.length + 1 &&
                (target[0] + target[2] === tld || target.slice(1) === tld || target.slice(0, 2) === tld)
            )) || null;
        }

        return null;
    }

    /**
     * A corrected domain, or null when the input looks fine.
     *
     * Four passes, each narrower than a plain distance match would be:
     *   0. exact hit in DOMAINS wins outright — never suggest against it
     *   A. DOMAIN_ALIASES, an exact-match table of known misspellings
     *   B. TLD correction, only for a TLD that is not itself valid
     *   C. second-level correction, with a threshold that refuses short labels
     *   D. whole-domain anchor, which fixes an otherwise-valid TLD (gmail.co)
     *      but only when the second-level part already matches exactly
     */
    function suggestDomain(domain) {
        if (!domain || DOMAINS.indexOf(domain) !== -1) return null;
        if (DOMAIN_ALIASES[domain]) return DOMAIN_ALIASES[domain];

        const parts = splitDomain(domain);
        if (!parts.sld || !parts.tld) return null;

        const tld = suggestTld(parts.tld) || parts.tld;
        const sld = (SECOND_LEVEL.indexOf(parts.sld) !== -1
            ? null
            : bestMatch(parts.sld, SECOND_LEVEL, secondLevelThreshold(parts.sld.length))
        ) || parts.sld;

        const candidate = [parts.prefix, sld, tld].filter(Boolean).join('.');

        // Pass D catches the case B cannot see: a TLD that is perfectly valid on
        // its own but wrong here, as in gmail.co. Requiring the second-level part
        // to match exactly keeps it from wandering — without that, line.com sits
        // one edit from live.com and would be "corrected".
        if (parts.labels === 2 && TLDS.indexOf(tld) !== -1 && MULTI_TLDS.indexOf(tld) === -1) {
            const anchored = bestMatch(candidate, DOMAINS, candidate.length <= 8 ? 1 : 2);
            if (anchored && splitDomain(anchored).sld === sld) {
                return anchored !== domain ? anchored : null;
            }
        }

        return candidate !== domain ? candidate : null;
    }

    /**
     * A corrected full address, or null.
     */
    function suggestEmail(email) {
        const at = String(email || '').lastIndexOf('@');
        if (at < 1) return null;

        const suggested = suggestDomain(email.slice(at + 1));
        return suggested ? email.slice(0, at + 1) + suggested : null;
    }

    /**
     * Clean an address for validation and for writing back into the input.
     *
     * Deliberately does NOT strip trailing punctuation — `a@b.com.` has to fail
     * as a hard error rather than be silently repaired. Only the domain is
     * lowercased; local-part case is preserved. (forms_routes.py lowercases the
     * whole address server-side anyway, so this is display fidelity.)
     */
    function normalize(raw) {
        let value = String(raw == null ? '' : raw);

        value = value.replace(/[\u200B-\u200D\uFEFF]/g, '');
        value = value.replace(/\s+/g, ' ').trim();
        value = value.replace(/^mailto:\s*/i, '');
        value = value.replace(/^[<([]\s*/, '').replace(/\s*[>)\]]$/, '');

        const at = value.lastIndexOf('@');
        if (at !== -1) {
            value = value.slice(0, at + 1) + value.slice(at + 1).toLowerCase();
        }

        return value.trim();
    }

    // The rule the forms have always used. Kept as a final gate so this module
    // can never be more permissive than the code it replaces.
    const LEGACY_PATTERN = /^[\+_a-z0-9-'&=]+(\.[\+_a-z0-9-']+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/i;

    /**
     * Structural validity. Catches the mistakes the legacy pattern lets through
     * or reports too late: doubled dots, a trailing comma, a bare domain.
     */
    function isSyntaxValid(email) {
        const value = String(email || '');
        if (!value || /\s/.test(value) || value.length > 254) return false;

        const at = value.indexOf('@');
        if (at < 1 || at !== value.lastIndexOf('@')) return false;

        const local = value.slice(0, at);
        const domain = value.slice(at + 1);
        if (!local || !domain || local.length > 64) return false;
        if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
        if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
        if (/[.,;:]$/.test(value)) return false;

        const labels = domain.split('.');
        if (labels.length < 2) return false;
        if (labels.some((label) => !label || label.startsWith('-') || label.endsWith('-'))) return false;
        if (!/^[a-z]{2,}$/i.test(labels[labels.length - 1])) return false;

        return LEGACY_PATTERN.test(value);
    }

    /**
     * @typedef {Object} BRXEmailResult
     * @property {boolean} ok         false only for hard failures
     * @property {string}  value      normalized address, safe to write back
     * @property {?string} error      shopper-facing message when !ok
     * @property {?string} suggestion full corrected address; never sets ok:false
     * @property {'valid'|'empty'|'syntax'} reason
     */

    /**
     * Normalize, then check syntax, then look for a likely typo.
     * A suggestion never sets ok:false — whether it blocks is up to the caller.
     */
    function check(raw, options) {
        const opts = options || {};
        const required = opts.required !== false;
        const value = normalize(raw);

        if (!value) {
            return {
                ok: !required,
                value,
                error: required ? MESSAGES.required : null,
                suggestion: null,
                reason: 'empty'
            };
        }

        if (!isSyntaxValid(value)) {
            return { ok: false, value, error: MESSAGES.invalid, suggestion: null, reason: 'syntax' };
        }

        return {
            ok: true,
            value,
            error: null,
            suggestion: opts.suggest === false ? null : suggestEmail(value),
            reason: 'valid'
        };
    }

    /** The error or hint node currently attached to this input, if any. */
    function existingNode(input) {
        if (!input) return null;
        return (input.id && document.getElementById(input.id + '-error')) ||
            (input.parentNode && input.parentNode.querySelector('._error')) ||
            null;
    }

    /**
     * Remove any error or hint attached to this input and reset its state.
     * Idempotent, so render() can call it freely.
     */
    function clear(input) {
        if (!input) return;

        const existing = existingNode(input);
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

        input.classList.remove('_has_error');
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
    }

    /**
     * Render an inline message under the input, optionally with a clickable
     * correction.
     *
     * Keeps the DOM shape the forms have always produced (._error wrapping
     * ._error-arrow and ._error-inner) so existing selectors still apply.
     *
     * @param {HTMLElement} input
     * @param {{ message: string, suggestion?: ?string, innerClass?: string,
     *           severity?: 'error'|'hint', role?: string,
     *           onAccept?: (value: string) => void }} opts
     */
    function render(input, opts) {
        if (!input || !input.parentNode) return null;

        const settings = opts || {};
        const hint = settings.severity === 'hint';

        // Reuse an identical node rather than rebuilding it. Tabbing from the
        // input to the suggestion button fires blur, which re-validates; without
        // this, the button would be destroyed just as focus moved onto it.
        const signature = [
            hint ? 'hint' : 'error',
            settings.message || '',
            settings.suggestion || '',
            settings.innerClass || ''
        ].join('::');

        const role = settings.role || (hint ? 'status' : 'alert');

        const current = existingNode(input);
        if (current && current.getAttribute('data-brx-signature') === signature) {
            // Role is updated in place rather than folded into the signature: a
            // hint raised on blur escalates to 'alert' once it blocks a submit,
            // and rebuilding the node for that would drop the very button focus
            // was moving to.
            current.setAttribute('role', role);
            return current;
        }
        clear(input);

        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-brx-signature', signature);
        wrapper.className = hint ? '_error _error--hint' : '_error';
        wrapper.setAttribute('role', role);
        if (input.id) wrapper.id = input.id + '-error';

        const arrow = document.createElement('div');
        arrow.className = '_error-arrow';
        wrapper.appendChild(arrow);

        const inner = document.createElement('div');
        inner.className = settings.innerClass ? '_error-inner ' + settings.innerClass : '_error-inner';

        const label = document.createElement('span');
        label.className = '_error-message';
        // textContent, never innerHTML: this string can carry server copy
        // derived from shopper input (did_you_mean, field_errors).
        label.textContent = settings.message || MESSAGES.generic;
        inner.appendChild(label);

        if (settings.suggestion) {
            const button = document.createElement('button');
            // type="button" is required — the element lives inside the <form>
            // and would otherwise submit it. Being a real button also gives
            // tab focus and Enter/Space activation for free.
            button.type = 'button';
            button.className = '_error-suggest';
            button.textContent = settings.suggestion;
            button.setAttribute('aria-label', 'Use ' + settings.suggestion);
            button.addEventListener('click', () => {
                input.value = settings.suggestion;
                clear(input);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
                input.focus();
                if (typeof settings.onAccept === 'function') settings.onAccept(settings.suggestion);
            });

            inner.appendChild(document.createTextNode(' '));
            inner.appendChild(button);
            inner.appendChild(document.createTextNode('?'));
        }

        wrapper.appendChild(inner);
        input.parentNode.appendChild(wrapper);

        if (input.id) input.setAttribute('aria-describedby', input.id + '-error');
        if (!hint) {
            input.classList.add('_has_error');
            input.setAttribute('aria-invalid', 'true');
        }

        return wrapper;
    }

    /**
     * Read an input, validate it, write the normalized value back, and render
     * the outcome. The single call both form snippets make on blur and submit.
     *
     * Never wire this to the `input` event — rewriting the value mid-typing
     * fights the shopper.
     *
     * @returns {BRXEmailResult}
     */
    function validateField(input, options) {
        const opts = options || {};
        const result = check(input ? input.value : '', opts);

        if (input && input.value !== result.value) input.value = result.value;

        if (!result.ok) {
            render(input, {
                message: result.error,
                innerClass: opts.innerClass,
                severity: 'error'
            });
        } else if (result.suggestion) {
            render(input, {
                message: MESSAGES.didYouMean,
                suggestion: result.suggestion,
                innerClass: opts.innerClass,
                severity: 'hint',
                role: opts.role,
                onAccept: opts.onAccept
            });
        } else {
            clear(input);
        }

        return result;
    }

    /**
     * Pull shopper-facing copy and a correction out of a 422 body.
     * @returns {{ message: string, suggestion: ?string }}
     */
    function parseServerRejection(json) {
        const data = (json && json.data) || {};
        const suggestion = (data.email_validation && data.email_validation.did_you_mean) || null;

        let message = (data.field_errors && data.field_errors.email && data.field_errors.email[0]) ||
            (json && json.errors && json.errors.email && json.errors.email[0]) ||
            (json && json.message) ||
            MESSAGES.generic;

        // The envelope's own message whenever field errors exist — not copy.
        if (message === 'Request validation failed') message = MESSAGES.generic;
        if (/^value is not a valid email address/i.test(message)) message = MESSAGES.invalid;

        return { message, suggestion };
    }

    window.BRXEmailValidation = {
        VERSION,
        MESSAGES,
        LISTS,
        normalize,
        isSyntaxValid,
        splitDomain,
        distance,
        suggestDomain,
        suggestEmail,
        check,
        render,
        clear,
        validateField,
        parseServerRejection
    };
})();
